import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  FormControl,
  Validators,
  FormArray,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { REGEX_CONSTANT } from '@sharedModule/constants/pattern-regex.constant';
import { MaterialModule } from '@sharedModule/material/material.module';
import { formatObjectNames } from '@sharedModule/utils/string.utils';
import { Subject, takeUntil } from 'rxjs';
import { DATA_TYPE } from '../../constant/adapter-settings.constant';
import { AdapterSettingsService } from '../../services/adapter-settings.service';
import { SettingsInterface } from '../../types/adapter-settings';
import {
  UpsertMasterAdapterSettingsPopupInterface,
  UpsertMasterAdapterSettingsInterface,
} from '../../types/upsert-adapter-settings';

@Component({
  selector: 'app-upsert-master-adapter-settings',
  standalone: true,
  imports: [InputModule, CommonModule, MaterialModule, ReactiveFormsModule],
  templateUrl: './upsert-master-adapter-settings.component.html',
  styleUrl: './upsert-master-adapter-settings.component.scss',
})
export class UpsertMasterAdapterSettingsComponent implements OnInit, OnDestroy {
  public readonly attrConstant = ATTRIBUTES_CONSTANT.settings.adapter;
  public readonly dataType = DATA_TYPE;
  upsertAdapterSettingFrom!: FormGroup;
  isEdit!: boolean;
  isSubmitLoading!: boolean;
  #destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private messageHandlerService: MessageHandlerService,
    private adapterSettingsService: AdapterSettingsService,
    @Inject(MAT_DIALOG_DATA)
    public adapterData: UpsertMasterAdapterSettingsPopupInterface,
    public closeDialogRef: MatDialogRef<UpsertMasterAdapterSettingsComponent>
  ) {}

  ngOnInit(): void {
    const isUpdate = this.adapterData.isUpdate;
    this.upsertAdapterSettingFrom = new FormGroup({
      [this.attrConstant.adapterType.key]: new FormControl(
        isUpdate ? this.adapterData.adapter_type : '',
        [Validators.required]
      ),
      [this.attrConstant.adapter.key]: new FormControl(
        isUpdate ? this.adapterData.adapter : '',
        [Validators.required]
      ),
      [this.attrConstant.description.key]: new FormControl(
        isUpdate ? this.adapterData.description : '',
        [Validators.required]
      ),
      settings: new FormArray([this.getSettingsData()]),
    });
    this.enforceUppercaseAdapterType();
    if (isUpdate) {
      this.patchValueToFormArray(this.adapterData.settings);
    }
  }

  // Adapter type must be uppercase with no spaces — transform as the user types.
  enforceUppercaseAdapterType(): void {
    const control = this.upsertAdapterSettingFrom.get(
      this.attrConstant.adapterType.key
    );
    if (!control) return;
    control.valueChanges
      .pipe(takeUntil(this.#destroy$))
      .subscribe((value: string) => {
        if (value == null) return;
        const transformed = value.toUpperCase().replace(/\s+/g, '');
        if (transformed !== value) {
          control.setValue(transformed, { emitEvent: false });
        }
      });
  }

  getSettingsData(): FormGroup {
    return this.fb.group({
      [this.attrConstant.name.key]: new FormControl('', [
        Validators.required,
        Validators.pattern(REGEX_CONSTANT.alphanumericTrimmed),
      ]),
      [this.attrConstant.dataType.key]: new FormControl('', [
        Validators.required,
      ]),
    });
  }

  addSettingRow(): void {
    this.settingData.push(this.getSettingsData());
  }

  removeSettingRow(i: number): void {
    this.settingData.removeAt(i);
  }

  get settingData(): FormArray {
    return this.upsertAdapterSettingFrom.controls['settings'] as FormArray;
  }

  patchValueToFormArray(settings: SettingsInterface[]): void {
    const control = this.upsertAdapterSettingFrom.controls[
      'settings'
    ] as FormArray;
    control.removeAt(0);
    for (const key in settings) {
      control.push(
        this.fb.group({
          [this.attrConstant.name.key]: new FormControl(settings[key].name, [
            Validators.required,
          ]),
          [this.attrConstant.dataType.key]: new FormControl(
            settings[key].data_type,
            [Validators.required]
          ),
        })
      );
    }
  }

  onSubmit(): void {
    if (this.upsertAdapterSettingFrom.valid) {
      this.isSubmitLoading = true;
      const payload = formatObjectNames(
        this.upsertAdapterSettingFrom.getRawValue()
      );
      if (this.adapterData.isUpdate) this.updateMasterAdapterSetting(payload);
      else this.addMasterAdapterSetting(payload);
    }
  }

  addMasterAdapterSetting(payload: UpsertMasterAdapterSettingsInterface): void {
    this.adapterSettingsService
      .addMasterAdapterSetting(payload)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.closeDialogRef.close(true);
          }
        },
        error: error => {
          console.log(error);
          this.closeDialogRef.close(false);
        },
      });
  }

  updateMasterAdapterSetting(
    payload: UpsertMasterAdapterSettingsInterface
  ): void {
    this.adapterSettingsService
      .updateMasterAdapterSetting({
        ...payload,
        code: this.adapterData.code,
      })
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.closeDialogRef.close(true);
          }
        },
        error: error => {
          console.log(error);
          this.closeDialogRef.close(false);
        },
      });
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
